import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/svenData";
import { EASE } from "./Reveal";

const LINES = ["Designed around", "You."];

export const Hero = () => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const tiltX = useSpring(
    useTransform(mx, [-0.5, 0.5], [-4, 4]),
    { stiffness: 60, damping: 20 }
  );

  const tiltY = useSpring(
    useTransform(my, [-0.5, 0.5], [-3, 3]),
    { stiffness: 60, damping: 20 }
  );

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;

    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      onMouseMove={onMouseMove}
      className="
        relative
        w-full
        overflow-hidden
        bg-[#fbfbfc]

        min-h-[720px]
        max-h-[980px]
        h-[100svh]

        lg:h-auto
        lg:aspect-video
        lg:min-h-0
      "
    >
      {/* Full-width Hero Image */}
      <motion.div
        style={{
          y: imgY,
          x: tiltX,
          translateY: tiltY,
        }}
        className="absolute inset-[-3%]"
      >
        <img
          src="/images/brand/banner_image.jpeg"
          alt="Sven Suits bespoke navy suit"
          className="
            w-full
            h-full
            object-cover
            object-[70%_20%]
          "
        />
      </motion.div>

      {/* Subtle bottom gradient for text readability */}
      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-[#0b0b0c]/15
          via-[#0b0b0c]/5
          to-transparent
        "
      />

      {/* Very subtle left-side readability gradient */}
      <div
        className="
          absolute
          inset-0
          bg-gradient-to-r
          from-[#0b0b0c]/45
          via-transparent
          to-transparent
        "
      />

      {/* Hero Content */}
      <motion.div
        style={{ opacity: fade }}
        className="
          relative
          z-10
          w-full
          h-full
          mx-auto

          px-4
          sm:px-6
          lg:px-10
          xl:px-12

          flex
          flex-col
          justify-end

          pb-24
          sm:pb-28
          lg:pb-24
        "
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.4,
            duration: 0.8,
            ease: EASE,
          }}
          className="overline-label mb-5"
        >
          Bespoke · Made To Measure · Designer
        </motion.p>

        <h1
          className="
            font-accent
            text-4xl
            sm:text-5xl
            lg:text-6xl
            xl:text-7xl
            text-white
            font-medium
            leading-[1.05]
            tracking-tight
            max-w-3xl
          "
        >
          {LINES.map((line, i) => (
            <span
              key={line}
              className="block overflow-hidden pb-1"
            >
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.55 + i * 0.16,
                  duration: 1.1,
                  ease: EASE,
                }}
              >
                {i === 1 ? (
                  <em className="font-accent font-medium text-[#dfcca4]">
                    {line}
                  </em>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 1.15,
            duration: 0.9,
            ease: EASE,
          }}
          className="
            mt-6
            max-w-xl
            text-sm
            sm:text-base
            font-light
            leading-relaxed
            text-white/75
          "
        >
          Bespoke suits, shirts, trousers and ceremonial tailoring
          created around your proportions, your wardrobe and your life.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 1.35,
            duration: 0.9,
            ease: EASE,
          }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-consultation-btn"
            onClick={() => scrollToId("book")}
            className="
              px-8
              py-3.5
              bg-[#c5a059]
              text-[#0b0b0c]
              font-mono-ov
              text-xs
              tracking-[0.18em]
              uppercase
              rounded-full
              hover:bg-[#dfcca4]
              hover:scale-[1.03]
              transition-all
              duration-300
            "
          >
            Book a Consultation
          </button>

          <button
            data-testid="hero-explore-collections-btn"
            onClick={() => scrollToId("collections")}
            className="
              px-8
              py-3.5
              border
              border-white/25
              text-white
              font-mono-ov
              text-xs
              tracking-[0.18em]
              uppercase
              rounded-full
              hover:border-[#c5a059]
              hover:text-[#dfcca4]
              transition-all
              duration-300
            "
          >
            Explore Collections
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom Information Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 2,
          duration: 1,
        }}
        className="
          absolute
          bottom-6
          sm:bottom-8
          left-0
          right-0
          z-10

          px-4
          sm:px-6
          lg:px-10
          xl:px-12

          flex
          items-center
          justify-between
        "
      >
        <p
          className="
            font-mono-ov
            text-[10px]
            tracking-[0.25em]
            uppercase
            text-white/50
          "
        >
          Santacruz West · Bandra West — 11 AM to 9 PM Daily
        </p>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{
            repeat: Infinity,
            duration: 2.2,
          }}
          className="text-[#c5a059]"
        >
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};